import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const API = '/api';

const BluIcon = ({ d, size = 18, fill = "none", strokeWidth = 2, color = 'currentColor' }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle' }}>
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ix = {
    building: ['M4 22h16', 'M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18', 'M10 10h4', 'M10 14h4'],
    card: ['M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z', 'M1 10h22'],
    wallet: ['M20 12V8H6a2 2 0 0 1 2-2h12V5a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2h-3', 'M20 12h-4a1 1 0 0 0 0 2h4v-2z'],
    ticket: ['M15 5v2', 'M15 11v2', 'M15 17v2', 'M5 5h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2z']
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0
    }).format(amount);
};

// Colors for the charts
const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4'];
const FONT = "'Outfit', 'Plus Jakarta Sans', -apple-system, sans-serif";
const ACCENT_COLOR = '#2563eb';

export default function Dashboard({ user, operatorDetails }) {
    const [operators, setOperators] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [opsRes, subsRes, tktRes, expRes] = await Promise.all([
                    axios.get(`${API}/operators`),
                    axios.get(`${API}/subscriptions`),
                    axios.get(`${API}/tickets`),
                    axios.get('/api/admin-expenses')
                ]);
                setOperators(opsRes.data || []);
                setSubscriptions(subsRes.data || []);
                setTickets(tktRes.data || []);
                setExpenses(expRes.data?.expenses || []);
            } catch (err) {
                console.error("Dashboard fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    // Derived Metrics
    const totalOperators = operators.length;
    const activeOperators = operators.filter(o => o.status === 'Active').length;
    const activeSubs = subscriptions.filter(s => s.status === 'Active').length;
    const totalRevenue = subscriptions.reduce((acc, curr) => acc + (curr.amount || curr.planId?.price || 0), 0);
    const totalExpenses = expenses.reduce((acc, curr) => acc + (curr.amount || 0), 0);
    const netProfit = totalRevenue - totalExpenses;
    const openTickets = tickets.filter(t => t.tags?.includes('To Superadmin') && t.status?.name !== 'Closed' && t.status?.name !== 'Resolved').length;

    // Process data for charts
    const processChartData = () => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentMonth = new Date().getMonth();
        const dataMap = {};
        
        for (let i = 5; i >= 0; i--) {
            let m = currentMonth - i;
            if (m < 0) m += 12;
            dataMap[months[m]] = { name: months[m], Revenue: 0, Expenses: 0 };
        }

        subscriptions.forEach(sub => {
            if (!sub.createdAt) return;
            const m = months[new Date(sub.createdAt).getMonth()];
            if (dataMap[m]) {
                dataMap[m].Revenue += (sub.amount || sub.planId?.price || 0);
            }
        });

        expenses.forEach(exp => {
            if (!exp.date) return;
            const m = months[new Date(exp.date).getMonth()];
            if (dataMap[m]) {
                dataMap[m].Expenses += (exp.amount || 0);
            }
        });

        return Object.values(dataMap);
    };

    const processTicketData = () => {
        const sysTkt = tickets.filter(t => t.tags?.includes('To Superadmin'));
        const counts = sysTkt.reduce((acc, curr) => {
            const stat = curr.status?.name || 'Open';
            acc[stat] = (acc[stat] || 0) + 1;
            return acc;
        }, {});
        return Object.keys(counts).map(key => ({ name: key, value: counts[key] }));
    };

    const chartData = processChartData();
    const ticketData = processTicketData();

    const recentOperators = [...operators].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6);
    const recentExpenses = [...expenses].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6);

    const Skeleton = () => (
        <div style={{ background: 'linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 6, height: '100%', width: '100%' }} />
    );

    return (
        <div className="superadmin-dash-page">
            <style>{`
                @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
                @keyframes fadeIn { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }

                .superadmin-dash-page {
                    background: #f8fafc;
                    min-height: 100vh;
                    padding: 28px 32px 48px;
                    font-family: ${FONT};
                    box-sizing: border-box;
                    overflow-y: auto;
                }

                /* Deep Navy Hero Banner */
                .dash-hero-banner {
                    background: linear-gradient(135deg, #0b1e3b 0%, #112d59 55%, #183d73 100%);
                    border-radius: 20px;
                    padding: 28px 32px;
                    color: white;
                    margin-bottom: 28px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    box-shadow: 0 12px 30px -6px rgba(11, 30, 59, 0.35), 0 4px 10px -2px rgba(11, 30, 59, 0.2);
                    position: relative;
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .dash-hero-banner::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: radial-gradient(circle at 92% 15%, rgba(59, 130, 246, 0.28) 0%, transparent 60%);
                    pointer-events: none;
                }

                .dash-hero-left {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .dash-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    border-radius: 9999px;
                    background: rgba(255, 255, 255, 0.12);
                    backdrop-filter: blur(8px);
                    color: #93c5fd;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    width: fit-content;
                    border: 1px solid rgba(255, 255, 255, 0.15);
                }

                .dash-hero-title {
                    margin: 0;
                    font-size: 28px;
                    font-weight: 800;
                    letter-spacing: -0.5px;
                    color: #ffffff;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }

                .dash-hero-subtitle {
                    margin: 0;
                    font-size: 14px;
                    color: #cbd5e1;
                    max-width: 620px;
                    line-height: 1.5;
                }

                .dash-hero-right {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .dash-hero-chip {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    background: rgba(255, 255, 255, 0.1);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.18);
                    padding: 10px 18px;
                    border-radius: 12px;
                }

                .dash-chip-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    background: #10b981;
                    box-shadow: 0 0 10px #10b981;
                }

                .dash-chip-text {
                    display: flex;
                    flex-direction: column;
                }

                .dash-chip-label {
                    font-size: 10.5px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #94a3b8;
                    font-weight: 700;
                }

                .dash-chip-val {
                    font-size: 13px;
                    font-weight: 800;
                    color: #ffffff;
                }

                /* Modern KPI Cards Grid */
                .dash-kpi-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                    margin-bottom: 28px;
                }

                @media (max-width: 1200px) {
                    .dash-kpi-grid { grid-template-columns: repeat(2, 1fr); }
                }
                @media (max-width: 640px) {
                    .dash-kpi-grid { grid-template-columns: 1fr; }
                }

                .dash-kpi-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 18px;
                    padding: 22px 24px;
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                    box-shadow: 0 4px 14px -2px rgba(15, 23, 42, 0.04), 0 2px 6px -2px rgba(15, 23, 42, 0.02);
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                    overflow: hidden;
                    animation: fadeIn 0.4s ease-out both;
                }

                .dash-kpi-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 24px -4px rgba(15, 23, 42, 0.08), 0 4px 8px -2px rgba(15, 23, 42, 0.03);
                    border-color: #cbd5e1;
                }

                .dash-kpi-top {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .dash-kpi-icon {
                    width: 46px;
                    height: 46px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .dash-kpi-pill {
                    font-size: 11px;
                    font-weight: 700;
                    padding: 3px 8px;
                    border-radius: 6px;
                }

                .dash-kpi-body {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .dash-kpi-label {
                    font-size: 12.5px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: #64748b;
                }

                .dash-kpi-value {
                    font-size: 30px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: -1px;
                    line-height: 1;
                }

                .dash-kpi-footer {
                    font-size: 12px;
                    color: #94a3b8;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    border-top: 1px solid #f1f5f9;
                    padding-top: 12px;
                }

                /* Charts & Sections */
                .dash-charts-grid {
                    display: grid;
                    grid-template-columns: 2fr 1fr;
                    gap: 24px;
                    margin-bottom: 28px;
                }

                @media (max-width: 1024px) {
                    .dash-charts-grid { grid-template-columns: 1fr; }
                }

                .dash-section-card {
                    background: #ffffff;
                    border-radius: 18px;
                    border: 1px solid #e2e8f0;
                    padding: 24px 26px;
                    box-shadow: 0 4px 14px -2px rgba(15, 23, 42, 0.04);
                    animation: fadeIn 0.4s ease-out both;
                }

                .dash-section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    border-bottom: 1px solid #f1f5f9;
                    padding-bottom: 16px;
                }

                .dash-section-title {
                    font-size: 17px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: -0.3px;
                    margin: 0;
                }

                .dash-section-sub {
                    font-size: 12.5px;
                    color: #64748b;
                    margin: 2px 0 0;
                }

                /* Tables */
                .dash-tables-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 24px;
                }

                @media (max-width: 1024px) {
                    .dash-tables-grid { grid-template-columns: 1fr; }
                }

                .dash-table-card {
                    background: #ffffff;
                    border-radius: 18px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 14px -2px rgba(15, 23, 42, 0.04);
                    overflow: hidden;
                    animation: fadeIn 0.4s ease-out both;
                }

                .dash-table-header-box {
                    padding: 20px 24px 16px;
                    border-bottom: 1px solid #f1f5f9;
                }

                .dash-modern-table {
                    width: 100%;
                    border-collapse: collapse;
                    text-align: left;
                }

                .dash-modern-table th {
                    padding: 12px 20px;
                    font-size: 11.5px;
                    font-weight: 700;
                    color: #64748b;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }

                .dash-modern-table td {
                    padding: 15px 20px;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 13.5px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                .dash-modern-table tr:hover td {
                    background: #f8fafc;
                }

                .dash-modern-table tr:last-child td {
                    border-bottom: none;
                }

                .dash-status-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 10px;
                    border-radius: 9999px;
                    font-size: 11.5px;
                    font-weight: 700;
                }

                .dash-status-pill.active {
                    background: #ecfdf5;
                    color: #059669;
                    border: 1px solid #a7f3d0;
                }

                .dash-status-pill.inactive {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }

                .dash-status-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: currentColor;
                }
            `}</style>

            {/* HERO BANNER */}
            <div className="dash-hero-banner">
                <div className="dash-hero-left">
                    <div className="dash-badge">
                        <BluIcon d={ix.building} size={13} strokeWidth={2.2} />
                        Super Admin Command Center
                    </div>
                    <h1 className="dash-hero-title">Platform Intelligence</h1>
                    <p className="dash-hero-subtitle">
                        Comprehensive telemetry, financial margins, recurring subscription health, and support workload overview.
                    </p>
                </div>
                <div className="dash-hero-right">
                    <div className="dash-hero-chip">
                        <div className="dash-chip-dot" />
                        <div className="dash-chip-text">
                            <span className="dash-chip-label">System Health</span>
                            <span className="dash-chip-val">100% Operational</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPI METRICS ROW */}
            <div className="dash-kpi-grid">
                {/* 1. Network Operators */}
                <div className="dash-kpi-card" style={{ animationDelay: '0.1s' }}>
                    <div className="dash-kpi-top">
                        <div className="dash-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                            <BluIcon d={ix.building} size={22} strokeWidth={2.2} />
                        </div>
                        <span className="dash-kpi-pill" style={{ background: '#eff6ff', color: '#2563eb' }}>
                            Tenants
                        </span>
                    </div>
                    <div className="dash-kpi-body">
                        <span className="dash-kpi-label">Network Operators</span>
                        <span className="dash-kpi-value">
                            {loading ? <div style={{ height: 32, width: 60 }}><Skeleton /></div> : totalOperators}
                        </span>
                    </div>
                    <div className="dash-kpi-footer">
                        <span style={{ color: '#10b981', fontWeight: 800 }}>{activeOperators}</span> Active live operators
                    </div>
                </div>

                {/* 2. Subscriptions */}
                <div className="dash-kpi-card" style={{ animationDelay: '0.2s' }}>
                    <div className="dash-kpi-top">
                        <div className="dash-kpi-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
                            <BluIcon d={ix.card} size={22} strokeWidth={2.2} />
                        </div>
                        <span className="dash-kpi-pill" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
                            Active MRR
                        </span>
                    </div>
                    <div className="dash-kpi-body">
                        <span className="dash-kpi-label">Subscriptions</span>
                        <span className="dash-kpi-value">
                            {loading ? <div style={{ height: 32, width: 60 }}><Skeleton /></div> : activeSubs}
                        </span>
                    </div>
                    <div className="dash-kpi-footer">
                        <span>Paying platform tenants</span>
                    </div>
                </div>

                {/* 3. Net Profit */}
                <div className="dash-kpi-card" style={{ animationDelay: '0.3s' }}>
                    <div className="dash-kpi-top">
                        <div className="dash-kpi-icon" style={{ background: '#ecfdf5', color: '#10b981' }}>
                            <BluIcon d={ix.wallet} size={22} strokeWidth={2.2} />
                        </div>
                        <span className="dash-kpi-pill" style={{ background: '#ecfdf5', color: '#10b981' }}>
                            Net Margin
                        </span>
                    </div>
                    <div className="dash-kpi-body">
                        <span className="dash-kpi-label">Net Profit</span>
                        <span className="dash-kpi-value">
                            {loading ? <div style={{ height: 32, width: 100 }}><Skeleton /></div> : formatCurrency(netProfit)}
                        </span>
                    </div>
                    <div className="dash-kpi-footer">
                        <span>Gross Revenue: <strong style={{ color: '#0f172a' }}>{formatCurrency(totalRevenue)}</strong></span>
                    </div>
                </div>

                {/* 4. Open Tickets */}
                <div className="dash-kpi-card" style={{ animationDelay: '0.4s' }}>
                    <div className="dash-kpi-top">
                        <div className="dash-kpi-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                            <BluIcon d={ix.ticket} size={22} strokeWidth={2.2} />
                        </div>
                        <span className="dash-kpi-pill" style={{ background: openTickets > 0 ? '#fef2f2' : '#ecfdf5', color: openTickets > 0 ? '#ef4444' : '#10b981' }}>
                            {openTickets > 0 ? 'Attention' : 'Clear'}
                        </span>
                    </div>
                    <div className="dash-kpi-body">
                        <span className="dash-kpi-label">Open Tickets</span>
                        <span className="dash-kpi-value">
                            {loading ? <div style={{ height: 32, width: 60 }}><Skeleton /></div> : openTickets}
                        </span>
                    </div>
                    <div className="dash-kpi-footer">
                        <span>Pending superadmin resolution</span>
                    </div>
                </div>
            </div>

            {/* CHARTS ROW */}
            <div className="dash-charts-grid">
                {/* Revenue vs Expenses Chart */}
                <div className="dash-section-card" style={{ animationDelay: '0.5s', height: 420 }}>
                    <div className="dash-section-header">
                        <div>
                            <h3 className="dash-section-title">Financial Trajectory</h3>
                            <p className="dash-section-sub">Monthly platform billing revenue vs operational expenses</p>
                        </div>
                    </div>
                    {loading ? (
                        <div style={{ height: 300, padding: 20 }}><Skeleton /></div>
                    ) : (
                        <ResponsiveContainer width="100%" height={310}>
                            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35}/>
                                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorExp" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.25}/>
                                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(value) => `$${value}`} />
                                <Tooltip 
                                    contentStyle={{ borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 12px 24px -4px rgba(15, 23, 42, 0.1)', fontFamily: FONT }}
                                    formatter={(value) => formatCurrency(value)}
                                />
                                <Legend verticalAlign="top" align="right" height={36} iconType="circle" wrapperStyle={{ fontSize: 12.5, fontWeight: 600 }} />
                                <Area type="monotone" dataKey="Revenue" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" />
                                <Area type="monotone" dataKey="Expenses" stroke="#ef4444" strokeWidth={2.5} fillOpacity={1} fill="url(#colorExp)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Support Tickets Distribution */}
                <div className="dash-section-card" style={{ animationDelay: '0.6s', height: 420 }}>
                    <div className="dash-section-header">
                        <div>
                            <h3 className="dash-section-title">Support Health</h3>
                            <p className="dash-section-sub">Ticket volume status split</p>
                        </div>
                    </div>
                    {loading ? (
                        <div style={{ height: 300, padding: 20 }}><Skeleton /></div>
                    ) : ticketData.length === 0 ? (
                        <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13.5, fontWeight: 500 }}>
                            No support tickets on record
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={310}>
                            <PieChart>
                                <Pie
                                    data={ticketData}
                                    cx="50%"
                                    cy="45%"
                                    innerRadius={70}
                                    outerRadius={95}
                                    paddingAngle={5}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {ticketData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip 
                                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 8px 16px -2px rgba(15, 23, 42, 0.08)', fontFamily: FONT }} 
                                    itemStyle={{ color: '#0f172a', fontWeight: 700 }}
                                />
                                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: 12.5, fontWeight: 600 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            {/* TABLES ROW */}
            <div className="dash-tables-grid">
                {/* Recent Operators */}
                <div className="dash-table-card" style={{ animationDelay: '0.7s' }}>
                    <div className="dash-table-header-box">
                        <h3 className="dash-section-title">Recent Operators</h3>
                        <p className="dash-section-sub">Newly registered platform tenants</p>
                    </div>
                    {loading ? (
                        <div style={{ padding: 24, height: 220 }}><Skeleton /></div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="dash-modern-table">
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Country</th>
                                        <th>Registered</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentOperators.length === 0 ? (
                                        <tr><td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8', padding: '30px' }}>No operators registered yet.</td></tr>
                                    ) : (
                                        recentOperators.map(op => (
                                            <tr key={op._id}>
                                                <td style={{ fontWeight: 700, color: '#0f172a' }}>{op.companyName || 'N/A'}</td>
                                                <td style={{ color: '#64748b' }}>{op.country || 'N/A'}</td>
                                                <td style={{ color: '#64748b' }}>{new Date(op.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <span className={`dash-status-pill ${op.status === 'Active' ? 'active' : 'inactive'}`}>
                                                        <span className="dash-status-dot" />
                                                        {op.status || 'Pending'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Recent System Expenses */}
                <div className="dash-table-card" style={{ animationDelay: '0.8s' }}>
                    <div className="dash-table-header-box">
                        <h3 className="dash-section-title">Recent Expenses</h3>
                        <p className="dash-section-sub">Latest administrative outgoing disbursements</p>
                    </div>
                    {loading ? (
                        <div style={{ padding: 24, height: 220 }}><Skeleton /></div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="dash-modern-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Category</th>
                                        <th>Payee</th>
                                        <th style={{ textAlign: 'right' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentExpenses.length === 0 ? (
                                        <tr><td colSpan="4" style={{ textAlign: 'center', color: '#94a3b8', padding: '30px' }}>No expenses recorded.</td></tr>
                                    ) : (
                                        recentExpenses.map(exp => (
                                            <tr key={exp._id}>
                                                <td style={{ color: '#64748b' }}>{new Date(exp.date).toLocaleDateString()}</td>
                                                <td style={{ fontWeight: 700, color: '#0f172a' }}>{exp.category}</td>
                                                <td style={{ color: '#64748b' }}>{exp.payee}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 800, color: '#ef4444' }}>
                                                    {formatCurrency(exp.amount)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
